import { useMemo, useState } from 'react';
import { fill } from '../../i18n/shell-texts';
import { useShellTexts } from '../../i18n/use-shell-texts';
import { inputClass } from './input';
import {
  buildChecklistChildValues,
  buildGroupedChecklistRows,
  buildVisibleChecklistOptions,
  checklistOptionValues,
  collectChecklistGroupKeys,
  collectChecklistSubtreeValues,
  isGroupedChecklist,
  type ChecklistRow,
} from './checklist-tree';
import type { FormFieldOption } from './schema';

/**
 * A searchable, possibly grouped list of checkboxes, ported from portal-ui-lib's
 * `ModalChecklistComponent`.
 *
 * - Grouped (`groupPath`): headings start collapsed; a heading's box selects everything under it.
 * - Tree (`parentValue`): selecting an option selects its subtree.
 * - `mergedValues`: one row standing for several records, selected together.
 * - Search matches label, badge and headings, and opens every group while it is on.
 *
 * Remount it (React `key`) to reset its search and expansion, as Angular's `resetKey` did.
 */
export function Checklist({
  options,
  value,
  onChange,
  searchable = false,
  searchPlaceholder,
  disabled = false,
  label,
}: {
  options: readonly FormFieldOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Accessible name of the list. */
  label?: string;
}) {
  const t = useShellTexts().kit;
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const grouped = isGroupedChecklist(options);
  const query = searchable ? search.trim().toLowerCase() : '';
  const selected = useMemo(() => new Set(value), [value]);
  const childValues = useMemo(() => buildChecklistChildValues(options), [options]);
  const total = useMemo(
    () => options.flatMap(option => checklistOptionValues(option)).length,
    [options],
  );

  const filtered = query
    ? options.filter(
        option =>
          option.label.toLowerCase().includes(query) ||
          (option.badge?.toLowerCase().includes(query) ?? false) ||
          (option.groupPath ?? []).some(segment => segment.toLowerCase().includes(query)),
      )
    : options;

  const rows: ChecklistRow[] = grouped
    ? buildGroupedChecklistRows(
        filtered,
        query ? [] : collectChecklistGroupKeys(filtered).filter(key => !expanded.has(key)),
      )
    : (query ? [...filtered] : buildVisibleChecklistOptions(options, [...collapsed])).map(
        option => ({
          kind: 'option' as const,
          option,
          depth: option.indentLevel ?? 0,
        }),
      );

  const apply = (values: readonly string[], checked: boolean) => {
    const next = new Set(value);
    for (const item of values) {
      if (checked) {
        next.add(item);
      } else {
        next.delete(item);
      }
    }
    onChange([...next]);
  };

  const toggle = (
    set: ReadonlySet<string>,
    key: string,
    update: (next: ReadonlySet<string>) => void,
  ) => {
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    update(next);
  };

  const chevron = (open: boolean) => (
    <svg
      className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );

  return (
    <div className="rounded-bs border border-input-border bg-white">
      {searchable || grouped ? (
        <div className="flex items-center gap-3 border-b border-line bg-cap p-2">
          {searchable ? (
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={searchPlaceholder ?? t.search}
              aria-label={searchPlaceholder ?? t.search}
              className={`${inputClass} flex-1`}
            />
          ) : null}
          <span className="text-xs whitespace-nowrap text-ink-muted">
            {fill(t.selectedCount, { selected: String(value.length), total: String(total) })}
          </span>
        </div>
      ) : null}
      <ul aria-label={label} className="max-h-80 overflow-y-auto p-2">
        {rows.length === 0 ? <li className="p-2 text-sm text-ink-muted">{t.noMatches}</li> : null}
        {rows.map(row => {
          if (row.kind === 'group') {
            const count = row.optionValues.filter(item => selected.has(item)).length;
            const open = Boolean(query) || expanded.has(row.key);
            return (
              <li
                key={`group:${row.key}`}
                className="flex items-center gap-2 py-1"
                style={{ paddingLeft: `${row.depth * 1.25}rem` }}
              >
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={`${open ? t.collapseGroup : t.expandGroup} ${row.label}`}
                  disabled={Boolean(query)}
                  onClick={() => toggle(expanded, row.key, setExpanded)}
                  className="text-ink-muted hover:text-ink disabled:opacity-40"
                >
                  {chevron(open)}
                </button>
                <input
                  type="checkbox"
                  aria-label={row.label}
                  disabled={disabled || row.optionValues.length === 0}
                  checked={row.optionValues.length > 0 && count === row.optionValues.length}
                  ref={element => {
                    if (element) {
                      element.indeterminate = count > 0 && count < row.optionValues.length;
                    }
                  }}
                  onChange={event => apply(row.optionValues, event.target.checked)}
                  className="h-4 w-4 accent-brand"
                />
                <span className="text-sm font-bold text-ink">{row.label}</span>
                {row.badge ? (
                  <span className="rounded-bs bg-line px-1.5 text-xs text-ink-soft">
                    {row.badge}
                  </span>
                ) : null}
              </li>
            );
          }
          const { option } = row;
          const values = checklistOptionValues(option);
          const hits = values.filter(item => selected.has(item)).length;
          const hasChildren = !grouped && (childValues.get(option.value)?.length ?? 0) > 0;
          const open = !collapsed.has(option.value);
          return (
            <li
              key={`option:${option.value}`}
              className="flex items-start gap-2 py-1"
              style={{ paddingLeft: `${row.depth * 1.25}rem` }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={`${open ? t.collapseGroup : t.expandGroup} ${option.label}`}
                  onClick={() => toggle(collapsed, option.value, setCollapsed)}
                  className="mt-0.5 text-ink-muted hover:text-ink"
                >
                  {chevron(open)}
                </button>
              ) : grouped ? null : (
                <span className="inline-block w-4" />
              )}
              <label className="mb-0 flex flex-1 cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={hits === values.length}
                  ref={element => {
                    if (element) {
                      element.indeterminate = hits > 0 && hits < values.length;
                    }
                  }}
                  onChange={event =>
                    apply(
                      grouped ? values : collectChecklistSubtreeValues(options, option.value),
                      event.target.checked,
                    )
                  }
                  className="mt-0.5 h-4 w-4 accent-brand"
                />
                <span>
                  {option.label}
                  {option.badge ? (
                    <span className="ml-2 rounded-bs bg-line px-1.5 text-xs text-ink-soft">
                      {option.badge}
                    </span>
                  ) : null}
                  {values.length > 1 ? (
                    <span
                      className="ml-2 text-xs text-ink-muted"
                      title={fill(t.mergedCount, { count: String(values.length) })}
                    >
                      ×{values.length}
                    </span>
                  ) : null}
                  {option.description ? (
                    <span className="block text-xs text-ink-muted">{option.description}</span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
